import { useEffect, useReducer, useRef } from 'react';

import { countItems } from '../services/items';
import { normalizeId } from '../utils/ids';
import { elementChoicesParam } from '../utils/elementRefusal';

/**
 * The key a list's total answers: the question the list asked — collection,
 * card filter, query, review filter, merged or not. useCollectionItems stamps
 * its `totalCount` with it (`totalKey`), so a total is only ever read as the
 * answer to the question it answered.
 */
export function listCountKey({ collectionId, cardFilter = null, query = '', review = null, includeLinked = false, extra = '' }) {
	const id = normalizeId(collectionId);
	return id ? JSON.stringify([id, cardFilter || '', query || '', review || '', includeLinked ? 1 : 0, ...(extra ? [extra] : [])]) : null;
}

// A side the server would not count. Merged, `?q=` compiles against the
// MERGED vocabulary (the collection's cards and the ones links lend it), so a
// query naming a borrowed card's element is a 400 on the collection's own
// count — the one side that can refuse. N is then unknown: the switch says
// "Include linked" with no number rather than a wrong one.
const REFUSED = Symbol('refused');

// How many questions' numbers to remember (a toggle back to a filter just
// left costs nothing; older ones are asked again).
const REMEMBERED = 12;

/**
 * HOW MANY RECORDS "INCLUDE LINKED" ADDS to this list (keepr-api § Merged
 * lists): the linked records that match the list's own card filter, search
 * and review filter — the merged count less the collection's own. The two
 * lists are disjoint (a linked row lives in another collection), so the
 * difference is exactly the linked records.
 *
 * ONE of the two numbers is already on screen: the list's own total
 * (`total`, stamped `totalKey` — see listCountKey) is the merged count when
 * the switch is on and the collection's own when it is off. So only the
 * OTHER side is asked — `GET /api/items/count` with the list's params, once
 * per question — and a toggle, whose other side is the total the list had a
 * moment ago, asks nothing. N is fixed once both are known: the list's total
 * moving under the same question (a record added or deleted here) moves both
 * sides alike, and re-deriving N from one fresh side and one stale one would
 * be wrong.
 *
 * Null until both are known, when disabled (nothing is linked), and when the
 * other side fails or is refused — the switch then says just "Include linked".
 */
export default function useLinkedCount({
	collectionId,
	cardFilter = null,
	query = '',
	review = null,
	enabled = true,
	includeLinked = false,
	total = null,
	totalKey = null,
	// The person's picks for an ambiguous element name in `query` (element
	// ids step 3, utils/elementRefusal.js): sent with it, as the list sends them.
	elementChoices = null,
}) {
	const id = normalizeId(collectionId);
	const choices = query ? elementChoicesParam(elementChoices) : undefined;
	const key = enabled && id ? JSON.stringify([id, cardFilter || '', query || '', review || '', ...(choices ? [choices] : [])]) : null;
	const listSide = includeLinked ? 'merged' : 'own';
	const otherSide = includeLinked ? 'own' : 'merged';
	const listTotal = key && totalKey && totalKey === listCountKey({ collectionId: id, cardFilter, query, review, includeLinked })
		&& Number.isFinite(total) ? total : undefined;

	// question -> { own, merged, asked: Set<side>, linked }
	const known = useRef(new Map());
	const [, rerender] = useReducer(n => n + 1, 0);

	useEffect(() => {
		if (!key) return undefined;
		const questions = known.current;
		let entry = questions.get(key);
		if (!entry) {
			entry = { own: undefined, merged: undefined, asked: new Set(), linked: undefined };
			questions.set(key, entry);
			if (questions.size > REMEMBERED) questions.delete(questions.keys().next().value);
		}
		const settle = () => {
			if (entry.linked !== undefined) return;
			const { own, merged } = entry;
			if (own === REFUSED || merged === REFUSED) entry.linked = null;
			else if (Number.isFinite(own) && Number.isFinite(merged)) entry.linked = Math.max(0, merged - own);
		};
		// The list's side, from the list's own total: no request.
		if (listTotal !== undefined && entry[listSide] === undefined) {
			entry[listSide] = listTotal;
			settle();
			rerender();
		}
		// The other side, asked once per question.
		if (entry[otherSide] === undefined && !entry.asked.has(otherSide)) {
			entry.asked.add(otherSide);
			const params = { collection_id: id };
			if (cardFilter) params.card_id = cardFilter;
			if (query) params.q = query;
			if (choices) params.elementChoices = choices;
			if (review) params.review = review;
			if (otherSide === 'merged') params.includeLinked = 1;
			Promise.resolve()
				.then(() => countItems(params))
				.then(count => { entry[otherSide] = count === null ? REFUSED : count; })
				.catch(() => { entry[otherSide] = REFUSED; })
				.then(() => { settle(); rerender(); });
		}
		return undefined;
	}, [key, listSide, otherSide, listTotal]); // eslint-disable-line react-hooks/exhaustive-deps

	if (!key) return null;
	const entry = known.current.get(key);
	return entry && entry.linked !== undefined ? entry.linked : null;
}
